// src/pages/install-app/InstallAppDevOverlay.tsx
import React, { useMemo, useState } from 'react';
import { DevOverlay, DevLabel, DevButton, DevMeta, DevMetaLine } from './install.styled';
import { ENVIRONMENT } from '../../../../env';

export type DeviceOverride = 'auto' | 'desktop' | 'android' | 'ios';

interface UseDeviceOverrideResult {
  deviceOverride: DeviceOverride;
  setDeviceOverride: (value: DeviceOverride) => void;
  effectiveIsIos: boolean;
  effectiveIsAndroid: boolean;
  effectiveIsDesktop: boolean;
  isDevEnv: boolean;
}

export const useDeviceOverride = (
  isIos: boolean,
  isAndroid: boolean,
  isDesktop: boolean
): UseDeviceOverrideResult => {
  const [deviceOverride, setDeviceOverride] = useState<DeviceOverride>('auto');

  const { effectiveIsIos, effectiveIsAndroid, effectiveIsDesktop } = useMemo(() => {
    if (deviceOverride === 'ios') {
      return { effectiveIsIos: true, effectiveIsAndroid: false, effectiveIsDesktop: false };
    }
    if (deviceOverride === 'android') {
      return { effectiveIsIos: false, effectiveIsAndroid: true, effectiveIsDesktop: false };
    }
    if (deviceOverride === 'desktop') {
      return { effectiveIsIos: false, effectiveIsAndroid: false, effectiveIsDesktop: true };
    }
    // auto → real detection
    return {
      effectiveIsIos: isIos,
      effectiveIsAndroid: isAndroid,
      effectiveIsDesktop: isDesktop,
    };
  }, [deviceOverride, isIos, isAndroid, isDesktop]);

  const isDevEnv = ENVIRONMENT === 'DEV';

  return {
    deviceOverride,
    setDeviceOverride,
    effectiveIsIos,
    effectiveIsAndroid,
    effectiveIsDesktop,
    isDevEnv,
  };
};

interface InstallAppDevOverlayProps {
  isDevEnv: boolean;
  deviceOverride: DeviceOverride;
  setDeviceOverride: (value: DeviceOverride) => void;
  isDesktop: boolean;
  isAndroid: boolean;
  isIos: boolean;
  manualPlatform: string | null | undefined;
  userAgent: string;
}

export const InstallAppDevOverlay: React.FC<InstallAppDevOverlayProps> = ({
  isDevEnv,
  deviceOverride,
  setDeviceOverride,
  isDesktop,
  isAndroid,
  isIos,
  manualPlatform,
  userAgent,
}) => {
  if (!isDevEnv) {
    return null;
  }

  return (
    <DevOverlay>
      <DevLabel>Device override:</DevLabel>
      <DevButton
        type="button"
        active={deviceOverride === 'auto'}
        onClick={() => setDeviceOverride('auto')}
      >
        Auto
      </DevButton>
      <DevButton
        type="button"
        active={deviceOverride === 'desktop'}
        onClick={() => setDeviceOverride('desktop')}
      >
        Desktop
      </DevButton>
      <DevButton
        type="button"
        active={deviceOverride === 'android'}
        onClick={() => setDeviceOverride('android')}
      >
        Android
      </DevButton>
      <DevButton
        type="button"
        active={deviceOverride === 'ios'}
        onClick={() => setDeviceOverride('ios')}
      >
        iOS
      </DevButton>

      <DevMeta>
        <DevMetaLine>
          real:{' '}
          {isDesktop
            ? 'desktop'
            : isAndroid
              ? 'android'
              : isIos
                ? 'ios'
                : (manualPlatform ?? 'unknown')}
        </DevMetaLine>
        <DevMetaLine>UA: {userAgent}</DevMetaLine>
      </DevMeta>
    </DevOverlay>
  );
};
